import { Component, OnDestroy, OnInit } from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { GepjarmuService } from '../gepjarmu.service';

/*  GÉPJÁRMŰ – ÚT RÖGZÍTÉSE GPS-SZEL (telefon).
 *
 *  A sofőr elindítja az utat, a végén leállítja, közben állomásokat jelölhet. A rendszer a telefon
 *  helymeghatározásából (navigator.geolocation.watchPosition) gyűjti a pontokat, haversine-nal számol
 *  össztávolságot, és az állomások közti szakaszokból KÉSZ út-rekordokat tölt be a meglévő
 *  útnyilvántartásba (POST /api/gepjarmu-ut/ut/:ugyfelId) – így a menetlevél/nyomtatás változatlanul kiírja.
 *
 *  Szándékosan a `navigator.geolocation`-t használja (nincs külön Capacitor-plugin függőség): a web és a
 *  Capacitor-WebView is kiszolgálja. Előtérben követ; háttérbe tevéskor az előtér-korlát miatt szünetelhet
 *  (ezt jelezzük) – a valódi háttérkövetés későbbi bővítés.
 *  Backend: backend/gepjarmu-ut.js. */

interface GpsPont { lat: number; lon: number; t: number; }
interface Allomas { cimke: string; km: number; ido: number; lat: number | null; lon: number | null; }

@Component({
  selector: 'app-gj-ut-gps',
  templateUrl: './ut-gps.component.html',
  styleUrls: ['../../naplo/naplo-shared.scss', '../gepjarmu-shared.scss', './ut-gps.component.scss'],
})
export class GjUtGpsComponent implements OnInit, OnDestroy {
  allapot: 'keszenlet' | 'rogzit' = 'keszenlet';
  torzs: any = { jarmuvek: [], soforok: [] };
  jarmuId = '';
  soforId = '';
  jelleg = 'uzleti';
  cel = '';

  osszKm = 0;
  pontDb = 0;
  pontossag: number | null = null;
  allomasok: Allomas[] = [];
  kezdetIdo = 0;
  most = 0;
  mentFut = false;

  private watchId: number | null = null;
  private elozo: GpsPont | null = null;
  private ticker: any = null;
  private sub?: Subscription;

  constructor(public gj: GepjarmuService, private toast: ToastController, private alert: AlertController) {}

  get u() { return this.gj.aktiv.ugyfelId; }
  get jarmu(): any { return this.torzs.jarmuvek.find((j: any) => j.id === this.jarmuId); }
  get aktivJarmuvek(): any[] { return (this.torzs.jarmuvek || []).filter((j: any) => +j.aktiv); }
  get aktivSoforok(): any[] { return (this.torzs.soforok || []).filter((s: any) => +s.aktiv); }

  get eltelt(): string {
    if (!this.kezdetIdo) return '0:00';
    const s = Math.max(0, Math.floor(((this.most || Date.now()) - this.kezdetIdo) / 1000));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return (h ? `${h}:${String(m).padStart(2, '0')}` : `${m}`) + ':' + String(sec).padStart(2, '0');
  }

  async ngOnInit() {
    await this.gj.init();
    this.sub = this.gj.aktiv$.subscribe(() => { if (this.allapot === 'keszenlet') this.torzsBetolt(); });
    await this.torzsBetolt();
  }
  ngOnDestroy() { this.sub?.unsubscribe(); this.leallitWatch(); }

  private async torzsBetolt() {
    if (!this.u) { this.torzs = { jarmuvek: [], soforok: [] }; return; }
    try {
      this.torzs = await this.gj.get(`ut:/torzs/${this.u}`);
      if (!this.aktivJarmuvek.some((j) => j.id === this.jarmuId)) this.jarmuId = (this.aktivJarmuvek[0] || this.torzs.jarmuvek[0])?.id || '';
      if (!this.soforId) this.soforId = this.jarmu?.alap_sofor_id || '';
    } catch { /* csendben – a készenléti űrlap üres marad */ }
  }

  /** Két pont közti távolság km-ben (haversine). */
  private tav(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
    const R = 6371;
    const rad = (x: number) => (x * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat), dLon = rad(b.lon - a.lon);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  async indit() {
    if (this.allapot === 'rogzit') return;
    if (!this.u) { this.uzen('Előbb válassz céget.', true); return; }
    if (!this.jarmuId) { this.uzen('Válassz járművet az út elindításához.', true); return; }
    if (!navigator.geolocation) { this.uzen('Ezen az eszközön nincs helymeghatározás.', true); return; }
    this.osszKm = 0; this.pontDb = 0; this.pontossag = null; this.elozo = null;
    this.kezdetIdo = Date.now(); this.most = this.kezdetIdo;
    this.allomasok = [{ cimke: 'Indulás', km: 0, ido: this.kezdetIdo, lat: null, lon: null }];
    this.allapot = 'rogzit';
    this.ticker = setInterval(() => { this.most = Date.now(); }, 1000);
    this.watchId = navigator.geolocation.watchPosition(
      (p) => this.pozicio(p),
      (e) => this.gpsHiba(e),
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 },
    );
    this.uzen('Rögzítés elindult – tartsd előtérben az appot.');
  }

  private pozicio(p: GeolocationPosition) {
    const c = p.coords;
    this.pontossag = Math.round(c.accuracy || 0);
    const cur: GpsPont = { lat: c.latitude, lon: c.longitude, t: p.timestamp };
    if (this.elozo) {
      const d = this.tav(this.elozo, cur); // km
      //  Zajszűrés: csak valós elmozdulást számolunk (nagyobb, mint a mért pontosság és > ~8 m).
      if (d * 1000 > Math.max(8, c.accuracy || 0)) { this.osszKm += d; this.elozo = cur; this.pontDb++; }
    } else {
      this.elozo = cur; this.pontDb++;
      if (this.allomasok[0] && this.allomasok[0].lat == null) { this.allomasok[0].lat = cur.lat; this.allomasok[0].lon = cur.lon; }
    }
  }

  private gpsHiba(e: GeolocationPositionError) {
    if (e.code === 1) this.uzen('A helymeghatározás nincs engedélyezve – engedélyezd az eszköz beállításaiban.', true);
    else if (e.code === 3) this.uzen('Gyenge GPS-jel (időtúllépés) – próbáld szabad ég alatt.', true);
    //  code 2 (ideiglenesen elérhetetlen): nem állítjuk le a rögzítést, a következő fix pótolja.
  }

  async allomas() {
    if (this.allapot !== 'rogzit') return;
    const alap = `${this.allomasok.length}. állomás`;
    const cimke = await this.cimkeKer('Állomás neve', alap);
    if (cimke === null) return; // Mégse
    this.allomasok.push({ cimke: cimke || alap, km: Math.round(this.osszKm * 10) / 10, ido: Date.now(), lat: this.elozo?.lat ?? null, lon: this.elozo?.lon ?? null });
    this.uzen('Állomás rögzítve.');
  }

  async leallit() {
    if (this.allapot !== 'rogzit') return;
    this.leallitWatch();
    const cimke = await this.cimkeKer('Érkezés helye', 'Érkezés');
    this.allomasok.push({ cimke: cimke || 'Érkezés', km: Math.round(this.osszKm * 10) / 10, ido: Date.now(), lat: this.elozo?.lat ?? null, lon: this.elozo?.lon ?? null });
    this.allapot = 'keszenlet';
    await this.mentes();
  }

  /** Megszakítás mentés nélkül (a rögzített adatok eldobásával). */
  async megszakit() {
    if (this.allapot !== 'rogzit') return;
    const a = await this.alert.create({
      header: 'Rögzítés megszakítása', message: 'Biztosan eldobod a most rögzített utat? Nem kerül az útnyilvántartásba.',
      buttons: [{ text: 'Vissza', role: 'cancel' }, { text: 'Eldobás', role: 'destructive', handler: () => { this.leallitWatch(); this.allapot = 'keszenlet'; this.alapra(); this.uzen('Rögzítés eldobva.'); } }],
    });
    await a.present();
  }

  private async mentes() {
    if (!this.u || !this.jarmuId) { this.uzen('Hiányzó cég vagy jármű – nem menthető.', true); return; }
    const datum = this.maiDatum();
    const szakaszok: { honnan: string; hova: string; km: number }[] = [];
    for (let i = 0; i < this.allomasok.length - 1; i++) {
      const a = this.allomasok[i], b = this.allomasok[i + 1];
      const km = Math.round((b.km - a.km) * 10) / 10;
      if (km > 0) szakaszok.push({ honnan: a.cimke || 'Indulás', hova: b.cimke || 'Érkezés', km });
    }
    if (!szakaszok.length) { this.uzen('Nem jött össze mérhető táv (legalább egy szakasz > 0 km kell). Az utat nem mentem.', true); this.alapra(); return; }
    this.mentFut = true;
    let ok = 0; const hibak: string[] = [];
    for (const sz of szakaszok) {
      try {
        await this.gj.post(`ut:/ut/${this.u}`, {
          jarmu_id: this.jarmuId, datum, sofor_id: this.soforId || null,
          honnan: sz.honnan, hova: sz.hova, km: sz.km, jelleg: this.jelleg, cel: this.cel || null, megjegyzes: 'GPS-rögzítés',
        });
        ok++;
      } catch (e: any) { hibak.push(e?.error?.error || 'ismeretlen hiba'); }
    }
    this.mentFut = false;
    if (ok) this.uzen(`${ok} útszakasz mentve az útnyilvántartásba${hibak.length ? `, ${hibak.length} sikertelen` : ''}.`);
    else this.uzen('Nem sikerült menteni az utat' + (hibak[0] ? `: ${hibak[0]}` : '.'), true);
    this.alapra();
  }

  private alapra() {
    this.osszKm = 0; this.pontDb = 0; this.pontossag = null; this.elozo = null; this.allomasok = []; this.kezdetIdo = 0; this.most = 0;
  }
  private leallitWatch() {
    if (this.watchId != null) { try { navigator.geolocation.clearWatch(this.watchId); } catch { /* nincs mit */ } this.watchId = null; }
    if (this.ticker) { clearInterval(this.ticker); this.ticker = null; }
  }
  private maiDatum(): string {
    const d = new Date(); const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }

  private cimkeKer(fejlec: string, alap: string): Promise<string | null> {
    return new Promise(async (resolve) => {
      const a = await this.alert.create({
        header: fejlec,
        inputs: [{ name: 'nev', type: 'text', value: '', placeholder: alap, attributes: { maxlength: 120 } }],
        buttons: [
          { text: 'Mégse', role: 'cancel', handler: () => resolve(null) },
          { text: 'OK', handler: (d) => resolve(String(d?.nev || '').trim()) },
        ],
      });
      await a.present();
    });
  }

  private async uzen(message: string, hiba = false) {
    (await this.toast.create({ message, duration: hiba ? 3500 : 2200, color: hiba ? 'danger' : 'success', position: 'top' })).present();
  }
}
